import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BreadcrumbProps } from '../../../@types';
import { AppContext } from '../../../context/app';
import { type Place, type PlaceGroup } from '../../../context/types';
import { useKioskManager } from '../../../hooks/useKioskManager';
import { useNetworkManager } from '../../../hooks/useNetworkManager';
import { ensureHttps } from '../../../utils/url';
import Breadcrumb from '../../shared/breadcrumb/Breadcrumb';
import CategoryButton from '../../shared/categoryButton/CategoryButton';
import { CategoryInfo } from '../../shared/categoryInfo/CategoryInfo';
import { Modal } from '../../shared/modal/Modal';
import { PlaceSearchStateEnum } from '../sectionGlobalSearch/types';
import SectionLocations from '../sectionLocations/SectionLocations';
import styles from './sectionCategories.module.scss';

type SectionCategoriesProps = {
  categories: PlaceGroup[];
};
export const SectionCategories: React.FC<SectionCategoriesProps> = ({
  categories,
}) => {
  const {
    selectedCategory,
    setSelectedCategory,
    setShowCTASection,
    setSelectedLocation,
    setPlaceSearchState,
    placeSearchState,
  } = useContext(AppContext);

  const { kiosk, kioskMode } = useKioskManager();
  const { placesData } = useNetworkManager();

  const { t } = useTranslation('common');
  const title = t('titles.campusCategories');

  const [isCategoryInfoOpen, setCategoryInfoOpen] = useState(false);
  const [categoryInfoData, setCategoryInfoData] = useState<PlaceGroup>(
    {} as PlaceGroup,
  );

  const handleSelectedCategoryPlaces = (category: PlaceGroup) => {
    setSelectedCategory(category);
    setShowCTASection(false);
    setPlaceSearchState(PlaceSearchStateEnum.CATAGORYFILTERED);
  };

  const clearSelectedCategory = () => {
    setSelectedCategory(undefined);
    setShowCTASection(true);
    setPlaceSearchState(PlaceSearchStateEnum.DISPLAYCATEGORIES);
  };

  const locationsByCategory = useMemo(
    () =>
      placesData?.filter((place: Place) =>
        selectedCategory?.places?.includes(place.id),
      ) ?? [],
    [selectedCategory, placesData],
  );

  const breadcrumbs: BreadcrumbProps[] = [
    {
      label: title,
      onClick: () => {
        clearSelectedCategory();
        setSelectedLocation(undefined);
      },
    },
    {
      label: selectedCategory?.name || '',
      onClick: () => '',
    },
  ];

  const handleCategoryClick = (category: PlaceGroup) => {
    if (category.places) {
      handleSelectedCategoryPlaces(category);
      return;
    }

    setCategoryInfoData(category);
    setCategoryInfoOpen(true);
  };

  return (
    <section
      className={`${styles.section} ${
        kiosk && kioskMode && styles[kiosk.model]
      }`}
    >
      <Modal isOpen={isCategoryInfoOpen}>
        <CategoryInfo
          body={categoryInfoData.information_body || ''}
          title={categoryInfoData.information_title || ''}
          closeButtonHandler={() => setCategoryInfoOpen(false)}
        />
      </Modal>
      {placeSearchState === PlaceSearchStateEnum.CATAGORYFILTERED ? (
        <>
          <Breadcrumb items={breadcrumbs} />
          <SectionLocations locations={locationsByCategory} />
        </>
      ) : (
        <>
          <Breadcrumb initialState />
          <div
            className={`${styles.list} ${
              kiosk && kioskMode && styles[kiosk.model]
            }`}
          >
            {categories
              ?.slice(0, 12)
              .map((category: PlaceGroup) => (
                <CategoryButton
                  key={category.name}
                  iconImage={ensureHttps(category.image_url)}
                  label={category.name}
                  onClick={() => handleCategoryClick(category)}
                />
              ))}
          </div>
        </>
      )}
    </section>
  );
};
